import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, ChevronDown, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DespachoInfoCallout,
  DespachoSectionCard,
  DespachoSimpleTable,
  DespachoStatusBadge,
} from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import {
  TASK_ADVANCE_OPTIONS,
  taskStatusLabel,
  taskStatusVariant,
} from "@/modules/despacho/operationalTaskHelpers";
import {
  workspaceUserDisplayName,
  mergeTasksReportByAuxiliarRows,
} from "@/modules/despacho/despachoWorkspaceAuxiliaries";
import { useDespachoWorkspaceAuxiliaries } from "@/modules/despacho/useDespachoWorkspaceAuxiliaries";
import { honorariosApi } from "@/modules/despacho/despachoHonorariosApi";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import TaskDetailDialog from "@/pages/despacho/TaskDetailDialog";
import { useAuth } from "@/lib/auth";
import { canManageWorkspaceTeam } from "@/lib/auxiliaryRoleGuide";
import { syncWorkClientFromDespacho } from "@/lib/clientSelection";

const DEFAULT_AD_HOC_TEMPLATES = [
  { key: "constancia", label: "Cambio en constancia de situación fiscal" },
  { key: "aclaracion", label: "Presentar aclaración" },
  { key: "alta_imss", label: "Alta / trámite IMSS" },
  { key: "alta_repse", label: "Alta / trámite REPSE" },
  { key: "otro", label: "Otro trámite especial" },
];
const SUB_RESUMEN = "resumen";
const SUB_MIS = "mis";
const SUB_LISTA = "lista";
const SUB_ESPECIAL = "especial";
const SUB_HISTORIAL = "historial";
const SUB_PLANTILLAS = "plantillas";
const SUB_GENERAR = "generar";

function mergeClientOptions(...sources) {
  const byId = new Map();
  for (const list of sources) {
    for (const row of list || []) {
      const id = row?.id || row?.fiscal_client_id;
      if (!id || byId.has(id)) continue;
      byId.set(id, {
        id,
        name: row.name || row.nombre || row.razon_social || row.client_name || id,
      });
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, "es"));
}

function flattenBoardClients(groups) {
  const out = [];
  for (const g of groups || []) {
    for (const row of g.items || []) {
      if (row?.fiscal_client_id) {
        out.push({
          id: row.fiscal_client_id,
          name: row.nombre || row.razon_social || row.client_name || row.fiscal_client_id,
        });
      }
    }
  }
  return out;
}

function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function isAuxiliarOperativo(user) {
  if (!user) return false;
  if (user.is_admin_owner) return false;
  if (user.is_buyer_tenant && user.role === "buyer_admin") return false;
  const dr = user.despacho_profile?.despacho_role || user.despacho_role;
  return dr === "auxiliar";
}

function defaultSubTabForUser(user) {
  if (isAuxiliarOperativo(user)) return SUB_MIS;
  if (canManageWorkspaceTeam(user)) return SUB_RESUMEN;
  return SUB_MIS;
}

function buildColumns({ readOnly, advancingId, onAdvance, onDetail }) {
  return [
    {
      key: "due_date",
      label: "Fecha",
      render: (r) => (r.due_date ? String(r.due_date).slice(0, 10) : "—"),
    },
    { key: "client_name", label: "Cliente", render: (r) => r.client_name || "—" },
    { key: "task_name", label: "Tarea", render: (r) => r.task_name || r.title || "—" },
    { key: "assigned_to_name", label: "Auxiliar", render: (r) => r.assigned_to_name || "—" },
    {
      key: "status",
      label: "Estado",
      render: (r) => (
        <DespachoStatusBadge variant={taskStatusVariant(r.status)}>
          {r.status_label || taskStatusLabel(r.status)}
        </DespachoStatusBadge>
      ),
    },
    {
      key: "source_label",
      label: "Tipo",
      render: (r) => r.source_label || "—",
    },
    {
      key: "_actions",
      label: "Acción",
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => onDetail(r)}>
            Ver
          </Button>
          {!readOnly ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-7"
                  disabled={advancingId === r.id || ["done", "cancelled", "not_applicable_this_period", "completada"].includes(r.status)}
                >
                  Avanzar
                  <ChevronDown className="h-3 w-3 ml-1" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {TASK_ADVANCE_OPTIONS.map((opt) => (
                  <DropdownMenuItem key={opt.value} onClick={() => onAdvance(r.id, opt.value)}>
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      ),
    },
  ];
}

export default function TareasInternasPanel({ readOnly = false, onTasksChanged }) {
  const { user } = useAuth();
  const isAuxiliar = isAuxiliarOperativo(user);
  const isTeamLead = canManageWorkspaceTeam(user);

  const [subTab, setSubTab] = useState(() => defaultSubTabForUser(user));
  const [tasks, setTasks] = useState([]);
  const [reportAux, setReportAux] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadMessage, setLoadMessage] = useState("");
  const [historyMonth, setHistoryMonth] = useState(currentMonthKey());
  const [advancingId, setAdvancingId] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const { auxiliares, reload: reloadAuxiliares } = useDespachoWorkspaceAuxiliaries();
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [metaLoadState, setMetaLoadState] = useState("idle");
  const [rules, setRules] = useState([]);
  const [catalogInternal, setCatalogInternal] = useState([]);

  const [adHocForm, setAdHocForm] = useState({
    template_key: "otro",
    title: "",
    fiscal_client_id: "",
    assigned_to_user_id: "",
    due_date: "",
    notes: "",
    initial_message: "",
    send_initial_reminder: true,
  });
  const [createBusy, setCreateBusy] = useState(false);

  const [genPeriodKey, setGenPeriodKey] = useState("");
  const [genPreview, setGenPreview] = useState(null);
  const [genPreviewState, setGenPreviewState] = useState("idle");
  const [genBusy, setGenBusy] = useState(false);

  const [ruleForm, setRuleForm] = useState({
    obligation_catalog_id: "",
    task_name: "",
    days_offset: 0,
  });
  const [ruleBusy, setRuleBusy] = useState(false);
  const templateOptions = useMemo(() => {
    const fromApi = (templates || []).filter((t) => t?.key);
    if (!fromApi.length) return DEFAULT_AD_HOC_TEMPLATES;
    const keys = new Set(fromApi.map((t) => t.key));
    const extras = DEFAULT_AD_HOC_TEMPLATES.filter((t) => !keys.has(t.key));
    return [...fromApi, ...extras];
  }, [templates]);

  const selectedTemplateKey = useMemo(() => {
    if (templateOptions.some((t) => t.key === adHocForm.template_key)) {
      return adHocForm.template_key;
    }
    return templateOptions[0]?.key || "otro";
  }, [templateOptions, adHocForm.template_key]);

  const selectedClientId = useMemo(() => {
    const id = adHocForm.fiscal_client_id;
    if (!id) return undefined;
    return clients.some((c) => c.id === id) ? id : undefined;
  }, [adHocForm.fiscal_client_id, clients]);

  const selectedAuxiliarId = useMemo(() => {
    const id = adHocForm.assigned_to_user_id;
    if (!id) return undefined;
    return auxiliares.some((a) => a.id === id) ? id : undefined;
  }, [adHocForm.assigned_to_user_id, auxiliares]);

  const listParams = useMemo(() => {
    const p = { internal_only: true };
    if (subTab === SUB_MIS && isAuxiliar) p.assigned_to_me = true;
    if (subTab === SUB_HISTORIAL && historyMonth.trim()) p.period_key = historyMonth.trim();
    return p;
  }, [subTab, historyMonth, isAuxiliar]);

  useEffect(() => {
    if (!user) return;
    const next = defaultSubTabForUser(user);
    setSubTab((prev) => {
      if (prev === SUB_RESUMEN && !canManageWorkspaceTeam(user)) return SUB_MIS;
      return next;
    });
  }, [user?.id]);

  const loadTasks = useCallback(async () => {
    if ([SUB_RESUMEN, SUB_PLANTILLAS, SUB_GENERAR, SUB_ESPECIAL].includes(subTab)) {
      setLoadState("ready");
      return;
    }
    setLoadState("loading");
    setLoadMessage("");
    try {
      const rows = await despachoFiscalInfoApi.listTasks(listParams);
      setTasks(rows);
      setLoadState("ready");
    } catch (err) {
      const parsed = parseFiscalInfoError(err);
      setTasks([]);
      setLoadState(parsed.kind === "forbidden" ? "forbidden" : "error");
      setLoadMessage(parsed.message);
    }
  }, [listParams, subTab]);

  const loadResumen = useCallback(async () => {
    try {
      const teamAux = isTeamLead ? await reloadAuxiliares() : auxiliares;
      const [auxRows, overdueRows] = await Promise.all([
        despachoFiscalInfoApi.listTasksReportByAuxiliar({ internal_only: true }),
        despachoFiscalInfoApi.listOverdueTasks(),
      ]);
      setReportAux(mergeTasksReportByAuxiliarRows(auxRows, teamAux));
      setOverdue(overdueRows.filter((t) => (
        t.task_domain === "ad_hoc"
        || t.task_domain === "interna"
        || String(t.obligation_catalog_id || "").startsWith("int_")
      )));
    } catch {
      setReportAux(mergeTasksReportByAuxiliarRows([], auxiliares));
      setOverdue([]);
    }
  }, [auxiliares, isTeamLead, reloadAuxiliares]);

  const loadMeta = useCallback(async () => {
    setMetaLoadState("loading");
    const results = await Promise.allSettled([
      despachoFiscalInfoApi.getBoard(),
      despachoFiscalInfoApi.listAdHocTemplates(),
      despachoFiscalInfoApi.listOperationalTaskRules({ internal_only: true }),
      despachoFiscalInfoApi.listCatalog({ authority: "Interna" }),
      honorariosApi.listClients(),
    ]);

    const [boardRes, tplsRes, rulesRes, catalogRes, honorClientsRes] = results;
    const boardClients = boardRes.status === "fulfilled"
      ? flattenBoardClients(boardRes.value?.groups)
      : [];
    const honorClients = honorClientsRes.status === "fulfilled" ? honorClientsRes.value : [];
    setClients(mergeClientOptions(boardClients, honorClients));

    if (tplsRes.status === "fulfilled" && tplsRes.value?.length) {
      setTemplates(tplsRes.value);
    } else {
      setTemplates([]);
    }

    setRules(rulesRes.status === "fulfilled" ? rulesRes.value : []);
    setCatalogInternal(catalogRes.status === "fulfilled" ? catalogRes.value : []);

    if (isTeamLead) {
      await reloadAuxiliares();
    }

    const failed = results.filter((r) => r.status === "rejected");
    setMetaLoadState("ready");
    if (failed.length && !boardClients.length && !honorClients.length) {
      toast.error("No se pudieron cargar empresas para la tarea especial.");
    } else if (failed.length) {
      toast.message("Algunos catálogos no cargaron; se usan valores disponibles.");
    }
  }, [isTeamLead, reloadAuxiliares]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (subTab === SUB_ESPECIAL) loadMeta();
  }, [subTab, loadMeta]);

  useEffect(() => {
    if (selectedTemplateKey && selectedTemplateKey !== adHocForm.template_key) {
      setAdHocForm((f) => ({ ...f, template_key: selectedTemplateKey }));
    }
  }, [selectedTemplateKey, adHocForm.template_key]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (subTab === SUB_RESUMEN) loadResumen();
  }, [subTab, loadResumen]);

  const handleAdvance = async (taskId, nextStatus) => {
    if (readOnly || !taskId) return;
    setAdvancingId(taskId);
    try {
      await despachoFiscalInfoApi.patchTask(taskId, { status: nextStatus });
      toast.success("Tarea actualizada.");
      await loadTasks();
      onTasksChanged?.();
      if (subTab === SUB_RESUMEN) await loadResumen();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setAdvancingId("");
    }
  };

  const handleCreateAdHoc = async (event) => {
    event.preventDefault();
    if (readOnly) return;
    const title = adHocForm.title.trim();
    if (!title) {
      toast.error("Indique el título o descripción de la tarea.");
      return;
    }
    if (!adHocForm.fiscal_client_id) {
      toast.error("Seleccione la empresa.");
      return;
    }
    if (!adHocForm.assigned_to_user_id) {
      toast.error("Seleccione el auxiliar responsable.");
      return;
    }
    setCreateBusy(true);
    try {
      const tpl = templateOptions.find((t) => t.key === adHocForm.template_key) || templateOptions[0];
      const result = await despachoFiscalInfoApi.createTask({
        title,
        task_domain: "ad_hoc",
        template_key: adHocForm.template_key,
        fiscal_client_id: adHocForm.fiscal_client_id,
        assigned_to_user_id: adHocForm.assigned_to_user_id,
        due_date: adHocForm.due_date || undefined,
        notes: adHocForm.notes.trim() || undefined,
        reminder_offsets: tpl?.default_offsets || [0, 2, 5, 7],
        send_initial_reminder: adHocForm.send_initial_reminder,
        initial_message: adHocForm.initial_message.trim() || title,
      });
      const sent = result?.followup?.alerts_sent ?? 0;
      toast.success(sent ? "Tarea especial creada y recordatorio enviado al chat." : "Tarea especial creada.");
      setAdHocForm((f) => ({
        ...f,
        title: "",
        notes: "",
        initial_message: "",
        due_date: "",
      }));
      setSubTab(SUB_MIS);
      onTasksChanged?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setCreateBusy(false);
    }
  };

  const runInternalPreview = async () => {
    setGenPreviewState("loading");
    setGenPreview(null);
    try {
      const body = { confirm: false };
      if (genPeriodKey.trim()) body.period_key = genPeriodKey.trim();
      const data = await despachoFiscalInfoApi.previewGenerateInternalTasks(body);
      setGenPreview(data);
      setGenPreviewState("ready");
    } catch (err) {
      setGenPreviewState("error");
      toast.error(parseFiscalInfoError(err).message);
    }
  };

  const confirmInternalGenerate = async () => {
    if (readOnly) return;
    setGenBusy(true);
    try {
      const uniqueInst = [
        ...new Set((genPreview?.tasks_to_create || []).map((t) => t.compliance_instance_id).filter(Boolean)),
      ];
      const result = await despachoFiscalInfoApi.generateInternalTasks({
        confirm: true,
        compliance_instance_ids: uniqueInst.length ? uniqueInst : undefined,
        period_key: genPeriodKey.trim() || undefined,
      });
      toast.success(`Se crearon ${result.created ?? 0} tarea(s) internas.`);
      setGenPreview(null);
      setGenPreviewState("idle");
      onTasksChanged?.();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setGenBusy(false);
    }
  };

  const handleCreateRule = async (event) => {
    event.preventDefault();
    if (readOnly || !ruleForm.obligation_catalog_id || !ruleForm.task_name.trim()) return;
    setRuleBusy(true);
    try {
      const data = await despachoFiscalInfoApi.createOperationalTaskRule({
        obligation_catalog_id: ruleForm.obligation_catalog_id,
        task_name: ruleForm.task_name.trim(),
        days_offset: Number(ruleForm.days_offset) || 0,
        based_on: "fecha_operativa",
      });
      setRules(data.items || []);
      setRuleForm({ obligation_catalog_id: "", task_name: "", days_offset: 0 });
      toast.success("Plantilla de tarea agregada.");
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setRuleBusy(false);
    }
  };

  const deactivateRule = async (ruleId) => {
    if (readOnly || !ruleId) return;
    try {
      const data = await despachoFiscalInfoApi.deleteOperationalTaskRule(ruleId);
      setRules(data.items || []);
      toast.success("Plantilla desactivada.");
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    }
  };

  const columns = useMemo(
    () => buildColumns({
      readOnly,
      advancingId,
      onAdvance: handleAdvance,
      onDetail: (r) => {
        setDetailId(r.id);
        setDetailOpen(true);
      },
    }),
    [readOnly, advancingId],
  );

  const openInternalTasks = useMemo(
    () => tasks.filter((t) => !["done", "cancelled", "not_applicable_this_period", "completada"].includes(t.status)),
    [tasks],
  );

  const misRows = useMemo(() => openInternalTasks, [openInternalTasks]);

  const carriedOver = useMemo(
    () => openInternalTasks.filter((t) => {
      const due = String(t.due_date || "").slice(0, 10);
      const today = new Date().toISOString().slice(0, 10);
      return due && due < today;
    }),
    [openInternalTasks],
  );

  return (
    <div className="space-y-4" data-testid="tareas-internas-panel">
      <DespachoInfoCallout testId="tareas-internas-notice">
        Control de <strong>tareas internas</strong> del despacho: papelería, revisiones, trámites especiales
        (constancia, IMSS, REPSE) con seguimiento al auxiliar asignado. Las obligaciones fiscales SAT se gestionan
        en la pestaña <strong>Fiscales</strong>.
      </DespachoInfoCallout>

      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList className="inline-flex flex-wrap h-auto gap-1 mb-4 rounded-sm border border-slate-200 bg-white p-1 w-full">
          <TabsTrigger value={SUB_MIS} className="text-xs rounded-sm data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white">
            {isAuxiliar ? "Mis pendientes" : "Pendientes activos"}
          </TabsTrigger>
          {isTeamLead ? (
            <TabsTrigger value={SUB_RESUMEN} className="text-xs rounded-sm data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white">
              Resumen equipo
            </TabsTrigger>
          ) : null}
          <TabsTrigger value={SUB_LISTA} className="text-xs rounded-sm data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white">Todas</TabsTrigger>
          <TabsTrigger value={SUB_ESPECIAL} className="text-xs rounded-sm data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white">Tarea especial</TabsTrigger>
          <TabsTrigger value={SUB_HISTORIAL} className="text-xs rounded-sm data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white">Historial</TabsTrigger>
          <TabsTrigger value={SUB_PLANTILLAS} className="text-xs rounded-sm data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white">Plantillas</TabsTrigger>
          <TabsTrigger value={SUB_GENERAR} className="text-xs rounded-sm data-[state=active]:bg-[#0F2D5C] data-[state=active]:text-white">Generar mes</TabsTrigger>
        </TabsList>

        <TabsContent value={SUB_MIS}>
          {loadState === "loading" ? (
            <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Cargando…
            </div>
          ) : null}
          {loadState === "error" || loadState === "forbidden" ? (
            <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
              <p className="m-0">{loadMessage}</p>
            </div>
          ) : null}
          {loadState === "ready" ? (
            <DespachoSectionCard
              title={isAuxiliar ? "Mis pendientes internos" : "Pendientes activos (internas)"}
              description={`${misRows.length} tarea(s)${isAuxiliar ? " asignadas a usted" : " abiertas del despacho"}`}
            >
              <DespachoSimpleTable
                columns={columns}
                rows={misRows}
                emptyLabel={
                  isTeamLead
                    ? "No hay tareas internas abiertas. Cree una en «Tarea especial» o genere el mes."
                    : "No tiene tareas internas pendientes."
                }
              />
            </DespachoSectionCard>
          ) : null}
        </TabsContent>

        <TabsContent value={SUB_RESUMEN}>
          <div className="grid gap-4 lg:grid-cols-2">
            <DespachoSectionCard title="Pendientes por auxiliar">
              <DespachoSimpleTable
                columns={[
                  { key: "auxiliar_name", label: "Auxiliar" },
                  { key: "pending", label: "Pendientes" },
                  { key: "in_progress", label: "En proceso" },
                  { key: "overdue", label: "Vencidas" },
                  { key: "done", label: "Terminadas" },
                ]}
                rows={reportAux.map((r, i) => ({ ...r, id: r.assigned_to_user_id || `aux-${i}` }))}
                emptyLabel={
                  auxiliares.length
                    ? "Auxiliares del equipo sin tareas internas registradas aún."
                    : "Sin auxiliares registrados. Dé de alta auxiliares en Equipo."
                }
              />
            </DespachoSectionCard>
            <DespachoSectionCard title="Vencidas / arrastradas (internas)" description={`${overdue.length} tarea(s)`}>
              <DespachoSimpleTable
                columns={[
                  { key: "client_name", label: "Cliente" },
                  { key: "task_name", label: "Tarea" },
                  { key: "assigned_to_name", label: "Auxiliar" },
                  { key: "due_date", label: "Vencía", render: (r) => String(r.due_date || "").slice(0, 10) },
                ]}
                rows={overdue.slice(0, 30).map((r) => ({ ...r, id: r.id }))}
                emptyLabel="No hay tareas internas vencidas."
              />
            </DespachoSectionCard>
          </div>
        </TabsContent>

        <TabsContent value={SUB_LISTA}>
          {loadState === "ready" ? (
            <DespachoSectionCard title="Todas las tareas internas" description={`${tasks.length} tarea(s)`}>
              <DespachoSimpleTable columns={columns} rows={tasks} emptyLabel="No hay tareas internas." />
            </DespachoSectionCard>
          ) : null}
        </TabsContent>

        <TabsContent value={SUB_ESPECIAL}>
          {subTab === SUB_ESPECIAL && !readOnly ? (
            <form onSubmit={handleCreateAdHoc} className="rounded-sm border border-slate-200 p-4 space-y-3 max-w-2xl">
              <p className="text-sm font-medium text-[#0F2D5C] m-0">Nueva tarea especial (comprador → auxiliar)</p>
              {metaLoadState === "loading" ? (
                <p className="text-xs text-slate-500 m-0 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  Cargando empresas, plantillas y auxiliares…
                </p>
              ) : null}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Plantilla</Label>
                  <Select
                    value={selectedTemplateKey}
                    onValueChange={(v) => setAdHocForm((f) => ({ ...f, template_key: v }))}
                  >
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {templateOptions.map((t) => (
                        <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="adhoc-due">Fecha compromiso</Label>
                  <Input
                    id="adhoc-due"
                    type="date"
                    value={adHocForm.due_date}
                    onChange={(e) => setAdHocForm((f) => ({ ...f, due_date: e.target.value }))}
                    className="rounded-sm"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="adhoc-title">Descripción</Label>
                <Input
                  id="adhoc-title"
                  value={adHocForm.title}
                  onChange={(e) => setAdHocForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Ej. Empresa X solicita cambio en constancia"
                  className="rounded-sm"
                  required
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Empresa</Label>
                  <Select
                    value={selectedClientId}
                    onValueChange={(v) => {
                      syncWorkClientFromDespacho(v);
                      setAdHocForm((f) => ({ ...f, fiscal_client_id: v }));
                    }}
                  >
                    <SelectTrigger className="rounded-sm"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Auxiliar</Label>
                  <Select
                    value={selectedAuxiliarId}
                    onValueChange={(v) => setAdHocForm((f) => ({ ...f, assigned_to_user_id: v }))}
                  >
                    <SelectTrigger className="rounded-sm"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                    <SelectContent>
                      {auxiliares.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.nombre || workspaceUserDisplayName(a)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <SpellcheckTextarea
                id="adhoc-message"
                label="Mensaje al auxiliar (chat interno)"
                value={adHocForm.initial_message}
                onChange={(e) => setAdHocForm((f) => ({ ...f, initial_message: e.target.value }))}
                rows={3}
                placeholder="Instrucciones concretas para el auxiliar…"
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={adHocForm.send_initial_reminder}
                  onChange={(e) => setAdHocForm((f) => ({ ...f, send_initial_reminder: e.target.checked }))}
                />
                Enviar recordatorio inicial al chat y programar seguimientos automáticos
              </label>
              <Button type="submit" size="sm" disabled={createBusy} className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]">
                {createBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden /> : <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />}
                Crear tarea especial
              </Button>
            </form>
          ) : subTab === SUB_ESPECIAL && readOnly ? (
            <p className="text-sm text-slate-600">Modo solo lectura.</p>
          ) : null}
        </TabsContent>

        <TabsContent value={SUB_HISTORIAL}>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="space-y-1">
              <Label htmlFor="hist-month">Periodo (YYYY-MM)</Label>
              <Input
                id="hist-month"
                value={historyMonth}
                onChange={(e) => setHistoryMonth(e.target.value)}
                placeholder="2026-06"
                className="rounded-sm w-40"
              />
            </div>
            <Button type="button" size="sm" variant="outline" onClick={loadTasks}>Consultar</Button>
          </div>
          {carriedOver.length ? (
            <DespachoInfoCallout className="mb-3">
              {carriedOver.length} tarea(s) del periodo siguen <strong>vencidas y abiertas</strong> (arrastradas).
            </DespachoInfoCallout>
          ) : null}
          {loadState === "ready" ? (
            <DespachoSectionCard title={`Historial ${historyMonth || "—"}`} description={`${tasks.length} tarea(s)`}>
              <DespachoSimpleTable columns={columns} rows={tasks} emptyLabel="Sin tareas en ese periodo." />
            </DespachoSectionCard>
          ) : null}
        </TabsContent>

        <TabsContent value={SUB_PLANTILLAS}>
          {subTab === SUB_PLANTILLAS && !readOnly ? (
            <form onSubmit={handleCreateRule} className="rounded-sm border border-slate-200 p-4 mb-4 space-y-3 max-w-2xl">
              <p className="text-sm font-medium m-0">Agregar paso operativo (espacio de trabajo)</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Obligación interna</Label>
                  <Select
                    value={ruleForm.obligation_catalog_id || undefined}
                    onValueChange={(v) => setRuleForm((f) => ({ ...f, obligation_catalog_id: v }))}
                  >
                    <SelectTrigger className="rounded-sm"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                    <SelectContent>
                      {catalogInternal.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.nombre_visible || c.short_name || c.id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Días respecto a fecha operativa</Label>
                  <Input
                    type="number"
                    value={ruleForm.days_offset}
                    onChange={(e) => setRuleForm((f) => ({ ...f, days_offset: e.target.value }))}
                    className="rounded-sm"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Nombre del paso</Label>
                <Input
                  value={ruleForm.task_name}
                  onChange={(e) => setRuleForm((f) => ({ ...f, task_name: e.target.value }))}
                  className="rounded-sm"
                  required
                />
              </div>
              <Button type="submit" size="sm" disabled={ruleBusy}>Agregar plantilla</Button>
            </form>
          ) : null}
          <DespachoSectionCard title="Plantillas de tareas internas" description={`${rules.length} regla(s)`}>
            <DespachoSimpleTable
              columns={[
                { key: "obligation_name", label: "Obligación" },
                { key: "task_name", label: "Paso" },
                { key: "days_offset", label: "Offset días" },
                {
                  key: "rule_scope",
                  label: "Ámbito",
                  render: (r) => (r.rule_scope === "global" ? "Global" : "Espacio de trabajo"),
                },
                {
                  key: "_act",
                  label: "",
                  render: (r) => (
                    r.editable && !readOnly ? (
                      <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => deactivateRule(r.id)}>
                        Desactivar
                      </Button>
                    ) : null
                  ),
                },
              ]}
              rows={rules.filter((r) => r.active !== false).map((r) => ({ ...r, id: r.id }))}
              emptyLabel="Sin plantillas."
            />
          </DespachoSectionCard>
        </TabsContent>

        <TabsContent value={SUB_GENERAR}>
          <DespachoInfoCallout className="mb-3">
            Genera tareas internas desde instancias del calendario operativo (obligaciones «Interna»).
            Primero genere vencimientos en Calendario → Operativo.
          </DespachoInfoCallout>
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <div className="space-y-1">
              <Label>Periodo YYYY-MM</Label>
              <Input
                value={genPeriodKey}
                onChange={(e) => setGenPeriodKey(e.target.value)}
                placeholder={currentMonthKey()}
                className="rounded-sm w-40"
              />
            </div>
            <Button type="button" onClick={runInternalPreview} disabled={genPreviewState === "loading"}>
              Vista previa
            </Button>
          </div>
          {genPreview ? (
            <DespachoSectionCard
              title="Vista previa"
              description={`${genPreview.summary?.new_count ?? 0} nuevas · ${genPreview.instances_selected ?? 0} instancias`}
            >
              <DespachoSimpleTable
                columns={[
                  { key: "task_name", label: "Tarea" },
                  { key: "fiscal_client_id", label: "Cliente" },
                  { key: "period", label: "Periodo" },
                  { key: "due_date", label: "Fecha" },
                ]}
                rows={(genPreview.tasks_to_create || []).slice(0, 60).map((r, i) => ({ ...r, id: `gp-${i}` }))}
                emptyLabel="No hay tareas nuevas."
              />
              {!readOnly ? (
                <Button
                  type="button"
                  className="mt-4"
                  onClick={confirmInternalGenerate}
                  disabled={genBusy || !(genPreview.tasks_to_create || []).length}
                >
                  {genBusy ? "Generando…" : "Confirmar generación interna"}
                </Button>
              ) : null}
            </DespachoSectionCard>
          ) : null}
        </TabsContent>
      </Tabs>

      <TaskDetailDialog
        taskId={detailId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onUpdated={() => {
          loadTasks();
          onTasksChanged?.();
        }}
        readOnly={readOnly}
      />
    </div>
  );
}
